// The address of one hub section, for a page that is not the hub (R818).
//
// The theme workshop and the map designer each have a ← HUB button, and it
// opens the hub on the MODDING section that names the page: WORKSHOP and
// MAP DESIGNER (§16.16). `fast/entry.ts` reads `?tab=` with `readSignIn`
// and opens the hub on that panel: at once for a session, and after the
// landing screen for a browser with no session. The hub reads `section`.

import type { PageKey, SectionAt, TabKey } from './hub/panel';
import type { PageId } from './options/sections';

/** The hub page, opened on one panel. A link that means one section of a
 *  panel in the section shell names it with `hubSectionHref`. */
export function hubTabHref(tab: TabKey): string {
  return `/?tab=${tab}`;
}

/** A hub panel in the section shell, opened on one of its sections
 *  (ui-map-interface.md §16.16): `/?tab=record&section=chronicle`. */
export function hubSectionHref(tab: TabKey, section: string): string {
  return `${hubTabHref(tab)}&section=${encodeURIComponent(section)}`;
}

/** The THEMES section of MODDING: the THEMES row of the in-game ACCOUNT
 *  page leads here. */
export const HUB_THEMES_HREF = hubSectionHref('mods', 'themes');

/** The SHOP panel of the hub, where the plans stand: SEE THE PLANS of the
 *  tier gate of the theme workshop leads here (R2134). */
export const HUB_SHOP_HREF = hubTabHref('shop');

/** Where ← HUB leads from the theme workshop: the WORKSHOP section. */
export const HUB_WORKSHOP_HREF = hubSectionHref('mods', 'workshop');

/** Where ← HUB leads from the map designer: the MAP DESIGNER section. */
export const HUB_DESIGNER_HREF = hubSectionHref('mods', 'designer');

/** The map designer page itself (R603). */
export const MAP_DESIGNER_HREF = '/mapdesigner';

/** The map designer, opened on one community map by its code in capitals
 *  (community-maps.md §9.1, R1975): OPEN IN DESIGNER of the arcade lobby.
 *  With no code it opens a new document. */
export function mapDesignerHref(code = ''): string {
  return code === '' ? MAP_DESIGNER_HREF : `${MAP_DESIGNER_HREF}?map=${encodeURIComponent(code.toUpperCase())}`;
}

/** The arcade page: its lobby. */
export const ARCADE_HREF = '/arcade';

/** The arcade page, opened on one hosting by its code (arcade.md §16.1). */
export function arcadeHostHref(code: string): string {
  return `${ARCADE_HREF}?host=${encodeURIComponent(code)}`;
}

/** The address parameter of a custom lobby (R3087, ui-map-interface.md
 *  §16.6): `/?lobby=3KX` opens the hub on the lobby of that code. The meta
 *  server writes the same word into the `link` of a lobby view. */
export const LOBBY_PARAM = 'lobby';

/** The hub, opened on one custom lobby by its code. The arcade PRIVATE
 *  JOIN sends a code with a digit first here. */
export function hubLobbyHref(code: string): string {
  return `/?${LOBBY_PARAM}=${encodeURIComponent(code)}`;
}

/** The theme workshop page. */
export const THEMES_HREF = '/themes';

/** The LADDER section of the arcade lobby, which holds the account's own
 *  ladder (arcade.md §18.6, R1948). RANKS links it from the ARCADE LADDER
 *  list. */
export const ARCADE_LADDER_HREF = `${ARCADE_HREF}?section=ladder`;

/** The DRILLS section of the arcade lobby, the drill board (arcade.md
 *  §17.8, R1931). The foot press of the HOW IT PLAYS card leads here
 *  (R2445). */
export const ARCADE_DRILLS_HREF = `${ARCADE_HREF}?section=drills`;

/**
 * The hub SETTINGS panel, opened on one of its sections (R1731). The
 * section ids are the ids of the ☸ OPTIONS menu (`ui/options/sections.ts`),
 * because the panel holds the same sections in the same order. The link
 * rows of the in-game ACCOUNT page land here: the sign-in methods, the
 * password and the account delete on `account`, the stated hours on `play`.
 */
export function hubSettingsHref(section: PageId): string {
  return hubSectionHref('settings', section);
}

/**
 * The address of a `PanelContext.go` target, for a page that leaves for
 * the hub (the cards aboard ☸ OPTIONS). Every page a panel opens on is a
 * section word of that panel too: a SETTINGS page, a RANKS board, the
 * chronicle of RECORD and the cup of CLAN.
 */
export function hubHref(tab: TabKey, at?: PageKey | SectionAt): string {
  if (at === undefined) return hubTabHref(tab);
  return hubSectionHref(tab, typeof at === 'string' ? at : at.section);
}
